(function () {
    'use strict';

    angular
        .module('app')
        .factory('ObjectSaveErrorHandlerService', ObjectSaveErrorHandlerService);

    ObjectSaveErrorHandlerService.$inject = ['$q', 'DialogService', 'EntitiesService'];

    /**
     * @ngdoc service
     * @name ObjectSaveErrorHandlerService
     * 
     * @description
     * Handle errors relating to object saving.
     *
     * # Optimistic Concurrency Control errors
     *
     * Optimistic concurrency control fails when a user loads a version (A) of an object (B),
     * modifies it, then tries to save it after somebody else has saved a new version (C).
     * Version of objects is determined by their "UpdatedDate" property, a value that is automatically
     * updated to the current date/time whenever an object is modified.
     */
    function ObjectSaveErrorHandlerService($q, DialogService, EntitiesService) {
        var service = {
            isResponseOptimisticConcurrencyError: isResponseOptimisticConcurrencyError,
            handleObjectSaveError: handleObjectSaveError
        };

        return service;


        /**
         * @param {object} httpResponse - HTTP service response including error details.
         * @param {object} objectBeingSaved - The object that was being saved. If the handling includes a retry, this object may be modified.
         * @param {string} objectName - The type/name of the object being saved, e.g. "document template". Used for wording in dialogs.
         * @param {function} saveCallback - Callback to retry save.
         * @param {function} getLatestCallback - Callback to get latest version of object being saved. Must return a promise that resolves
         *                   the latest version of the object.
         *
         * @returns {promise} Resolves if object save was successfully retried, otherwise rejected.
         */
        function handleObjectSaveError(httpResponse, objectBeingSaved, objectName, saveCallback, getLatestCallback) {

            //
            // Validate Input
            //

            if (!angular.isDefined(httpResponse) ||
                !angular.isObject(httpResponse)) {

                throw 'handleObjectSaveError - httpResponse object does not have any response data';
            }

            if (_.isNil(objectBeingSaved)) {
                throw 'handleObjectSaveError - objectBeingSaved is null';
            }

            if (_.isNil(objectName)) {
                objectName = 'object';
            }

            if (!angular.isFunction(saveCallback)) {
                throw 'handleObjectSaveError - saveCallback is not a function';
            }

            if (!angular.isFunction(getLatestCallback)) {
                throw 'handleObjectSaveError - getLatestCallback is not a function';
            }


            //
            // Process Response
            //

            if (isResponseOptimisticConcurrencyError(httpResponse)) {
                return getLatest().then(getLatestOk, getLatestError);
            } else if (isResponseNotFoundError(httpResponse)) {
                return showNotFoundError(httpResponse);
            } else {
                return showSaveError(httpResponse);
            }

            /**
             * For optimistic concurrency error recovery.
             * If the latest version could be retrieved, run this.
             */
            function getLatestOk(latest) {
                var optimisticConcurrencyDialog = DialogService.show({
                    locals: {
                        objectType: objectName,
                        objectBeingSaved: objectBeingSaved,
                        latestVersion: latest
                    },
                    controller: 'OptimisticConcurrencyDialogCtrl',
                    controllerAs: 'vm',
                    templateUrl: 'app/controls/dialog/optimistic-concurrency-dialog.tmpl.html'
                });

                return optimisticConcurrencyDialog
                    .then(function () {
                        // User clicked 'OK' in dialog, so retry save of object
                        objectBeingSaved.UpdatedDate = latest.UpdatedDate;
                        return saveCallback();
                    });
            }

            /**
             * For optimistic concurrency error recovery.
             * If the latest version could not be retrieved, run this.
             */
            function getLatestError(response) {
                if (isResponseNotFoundError(response)) {
                    return showNotFoundError(response);
                } else {
                    return showSaveError(response);
                }
            }

            /**
             * Get the latest version of the object being saved.
             * Most relevant after an optimistic concurrency fail.
             *
             * @returns {promise} Promise resolving an {object} with the following properties:
             *   - {date} UpdatedDate
             *   - {number} UpdatedBy - May be null if the object was not updated by a user.
             *   - {string} UpdatedByPreviewNameText - May be null if details of the entity cannot be found or UpdatedBy is null.
             *   Rejects with an HTTP service response if there was an error retrieving the latest version of the object.
             */
            function getLatest() {
                return getLatestCallback()
                    .then(getLatestCallbackOk);

                function getLatestCallbackOk(data) {
                    var latestData = {};
                    latestData.UpdatedDate = data.UpdatedDate;
                    latestData.UpdatedBy = data.UpdatedBy;

                    if (_.isNil(data.UpdatedBy)) {
                        return latestData;
                    } else {
                        return getEntityPreviewText(data.UpdatedBy).then(
                            function ok(previewText) {
                                latestData.UpdatedByPreviewNameText = previewText;
                                return latestData;
                            },
                            function error() {
                                latestData.UpdatedByPreviewNameText = null;
                                return latestData;
                            }
                        );
                    }
                }

                function getEntityPreviewText(entityId) {
                    return EntitiesService.getPreviewText(entityId).then(
                        function ok(data) {
                            return data.PreviewNameText;
                        },
                        function error() {
                            return null;
                        }
                    );
                }
            }

            /**
             * Show a dialog indicating error saving the object because it could not be found.
             */
            function showNotFoundError(httpResponse) {
                return DialogService.error('Error saving ' + objectName + '.<br/>The ' + objectName + ' could not be found')
                    .then(function () {
                        return $q.reject(httpResponse);
                    });
            }

            /**
             * Show a dialog indicating error saving the object. Show details of the error if available.
             */
            function showSaveError(httpResponse) {
                return DialogService.error('Error saving ' + objectName, httpResponse)
                    .then(function () {
                        return $q.reject(httpResponse);
                    });
            }
        }

        /**
         * Determine whether the response object is an error indicating optimistic concurrency control fail.
         *
         * @param {object} httpResponse - A HTTP error response.
         * @returns {bool}
         */
        function isResponseOptimisticConcurrencyError(httpResponse) {
            return angular.isDefined(httpResponse) &&
                    angular.isObject(httpResponse) &&
                    httpResponse.data &&
                    httpResponse.data.ExceptionType === 'ChilliDocs.Models.Exceptions.OptimisticConcurrencyException';
        }

        /**
         * Determine whether the response object is an error indicating 404: Not Found.
         *
         * @param {object} httpResponse - A HTTP error response.
         * @returns {bool}
         */
        function isResponseNotFoundError(httpResponse) {
            return angular.isDefined(httpResponse) &&
                    angular.isObject(httpResponse) &&
                    httpResponse.status === 404;
        }
    }
})();
